/**
 * API Service - Handles all HTTP requests to the backend API
 * Provides a centralized interface for API calls with error handling
 */

import type {
  Graph,
  HierarchyNode,
  NodeTypesResponse,
  RelationshipTypesResponse,
  TypeRules,
  AllowedTargetsResponse,
  ProposalActionRequest,
  ProposalSession,
  ProposalStep,
  ProposalStepGroup,
  SessionTree,
  BatchProposalItem,
  SearchResult,
  CurationIndex,
  CurationDocument,
  FoundryResponseDetails,
  RejectionRecord,
  JustificationRecord,
  AnalyzeSessionRequest,
  AnalyzeSessionResponse,
  GraphSearchResponse,
  SummarizeNodeRequest,
  SummarizeNodeResponse,
  BackfillSummariesRequest,
  BackfillSummariesResponse,
  AddFreeformDocumentRequest,
  AddFreeformDocumentResponse,
  SystemLookupResult,
} from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8097';
const API_VERSION = 'v1';

// ---------------------------------------------------------------------------
// Token injection — AuthContext registers a getter so all API calls can attach
// an Authorization header without passing tokens through every call site.
// ---------------------------------------------------------------------------
let _tokenGetter: (() => Promise<string>) | null = null;

/** Called by AuthContext on mount to register the token getter. */
export function setTokenGetter(fn: () => Promise<string>): void {
  _tokenGetter = fn;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (!_tokenGetter) return {};
  try {
    const token = await _tokenGetter();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/**
 * Base fetch wrapper with error handling.
 * Uses a generic parameter so callers get typed responses.
 */
async function apiFetch<T = unknown>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE_URL}/api/${API_VERSION}${endpoint}`;
  const authHeaders = await getAuthHeaders();

  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers as Record<string, string>),
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        message?: string;
        error_detail?: string;
      };
      const message = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      const detail = errorData.error_detail?.trim();
      throw new Error(detail ? `${message}: ${detail}` : message);
    }

    const responseText = await response.text();
    if (!responseText.trim()) {
      return undefined as T;
    }

    return JSON.parse(responseText) as T;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
}

async function mcpFetch<T = unknown>(toolName: string, args: Record<string, unknown>): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/map/mcp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: args,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const payload = (await response.json()) as {
    error?: { message?: string };
    result?: { content?: Array<{ text?: string }> };
  };
  if (payload.error) {
    throw new Error(payload.error.message || `MCP ${toolName} failed`);
  }
  const text = payload.result?.content?.[0]?.text;
  if (!text) {
    throw new Error(`MCP ${toolName} returned no content`);
  }

  const parsed = parseMcpContent<T>(text);
  if (parsed !== null) {
    return parsed;
  }

  throw new Error(`MCP ${toolName} returned non-JSON content`);
}

function parseMcpContent<T>(text: string): T | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  const direct = tryParseJson<T>(trimmed);
  if (direct !== null) {
    return direct;
  }

  const firstObject = trimmed.indexOf('{');
  const lastObject = trimmed.lastIndexOf('}');
  if (firstObject >= 0 && lastObject > firstObject) {
    const objectSlice = trimmed.slice(firstObject, lastObject + 1);
    const objectValue = tryParseJson<T>(objectSlice);
    if (objectValue !== null) {
      return objectValue;
    }
  }

  const firstArray = trimmed.indexOf('[');
  const lastArray = trimmed.lastIndexOf(']');
  if (firstArray >= 0 && lastArray > firstArray) {
    const arraySlice = trimmed.slice(firstArray, lastArray + 1);
    const arrayValue = tryParseJson<T>(arraySlice);
    if (arrayValue !== null) {
      return arrayValue;
    }
  }

  return null;
}

function tryParseJson<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * Graph API methods
 */
export const graphApi = {
  /**
   * Get the entire graph or a subgraph
   */
  getGraph: async (filters: Record<string, string> = {}): Promise<Graph> => {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = queryParams ? `/graph?${queryParams}` : '/graph';
    return apiFetch<Graph>(endpoint);
  },

  /**
   * Get a specific node by ID with its immediate neighbors
   */
  getNodeWithNeighbors: async (nodeId: string): Promise<Graph> => {
    return apiFetch<Graph>(`/graph/nodes/${nodeId}/neighbors`);
  },

  /**
   * Get the hierarchical graph structure for icicle view
   */
  getHierarchy: async (): Promise<HierarchyNode> => {
    return apiFetch<HierarchyNode>('/graph/hierarchy');
  },
};

type FoundryResponseToolCallApiModel = {
  name?: string;
  status?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  arguments_preview?: string;
  result_preview?: string;
  raw_arguments?: unknown;
  raw_result?: unknown;
  error?: string;
};

type FoundryResponseDetailsApiModel = {
  session_id?: string;
  response_id?: string;
  status?: string;
  agent_name?: string;
  model?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  error?: string;
  final_output?: string;
  tool_calls?: FoundryResponseToolCallApiModel[];
  raw?: unknown;
};

/**
 * Type API methods
 */
export const typeApi = {
  /**
   * Get all node types. Response is { node_types: NodeType[] }.
   */
  getNodeTypes: async (): Promise<NodeTypesResponse> => {
    return apiFetch<NodeTypesResponse>('/types/nodes');
  },

  /**
   * Get all relationship types. Response is { relationship_types: RelationshipType[] }.
   */
  getRelationshipTypes: async (): Promise<RelationshipTypesResponse> => {
    return apiFetch<RelationshipTypesResponse>('/types/relationships');
  },

  getTypeRules: async (): Promise<TypeRules> => {
    return apiFetch<TypeRules>('/types/rules');
  },

  updateTypeRules: async (
    rules: TypeRules
  ): Promise<{ updated: boolean; node_types: number; relationship_types: number }> => {
    const resp = await apiFetch<{
      data: { updated: boolean; node_types: number; relationship_types: number };
    }>('/types/rules', {
      method: 'PUT',
      body: JSON.stringify({ type_rules: rules }),
    });
    return resp.data;
  },

  /**
   * Get allowed target node IDs for a given source node and relationship type
   */
  getAllowedTargets: async (
    sourceId: string,
    relationshipType: string
  ): Promise<AllowedTargetsResponse> => {
    const params = new URLSearchParams({
      source_id: sourceId,
      relationship_type: relationshipType,
    });
    return apiFetch<AllowedTargetsResponse>(`/validation/allowed-targets?${params.toString()}`);
  },
};

/**
 * Proposal API methods (mcp-flow curator workflow)
 */
export const proposalApi = {
  createSession: async (
    req: { session_id?: string; depends_upon_session?: string; title?: string } = {}
  ): Promise<ProposalSession> => {
    const resp = await apiFetch<{ data: SessionApiModel }>('/sessions', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return mapProposalSession(resp.data);
  },

  submitSessionBatch: async (
    sessionId: string,
    req: {
      proposals: BatchProposalItem[];
      title?: string;
      source_type?: 'document';
      index_name?: string;
      document_id?: string;
      chunk_id?: string;
      source?: string;
    }
  ): Promise<{ message: string; session_id: string; status: string }> => {
    const resp = await apiFetch<{ data: { message: string; session_id: string; status: string } }>(
      `/agents/gap/sessions/${sessionId}/proposals`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  submitSession: async (
    sessionId: string,
    req: { title?: string } = {}
  ): Promise<{ message: string; session_id: string }> => {
    const resp = await apiFetch<{ data: { message: string; session_id: string } }>(
      `/sessions/${sessionId}/submit`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  deleteSession: async (sessionId: string): Promise<void> => {
    await apiFetch<void>(`/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  },
  /**
   * Approve all proposals in a session atomically (curator only).
   */
  approveSession: async (sessionId: string): Promise<{ message: string; count: number }> => {
    const resp = await apiFetch<{ data: { message: string; count: number } }>(
      `/sessions/${sessionId}/approve`,
      {
        method: 'POST',
      }
    );
    return resp.data;
  },

  /**
   * Reject all proposals in a session atomically (curator only).
   */
  rejectSession: async (
    sessionId: string,
    req: ProposalActionRequest
  ): Promise<{ message: string; count: number; warning?: string }> => {
    const resp = await apiFetch<{ data: { message: string; count: number; warning?: string } }>(
      `/sessions/${sessionId}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  cancelSession: async (
    sessionId: string,
    req?: ProposalActionRequest
  ): Promise<{ message: string; count: number }> => {
    const resp = await apiFetch<{ data: { message: string; count: number } }>(
      `/sessions/${sessionId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(req ?? {}),
      }
    );
    return resp.data;
  },

  rejectSessionStep: async (
    sessionId: string,
    req: { proposal_step_id: string; rejection_reason: string }
  ): Promise<{ message: string; count: number; proposal_step_id: string }> => {
    const resp = await apiFetch<{
      data: { message: string; count: number; proposal_step_id: string };
    }>(`/sessions/${sessionId}/steps/reject`, {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return resp.data;
  },

  unrejectSessionStep: async (
    sessionId: string,
    req: { proposal_step_id: string }
  ): Promise<{ message: string; proposal_step_id: string }> => {
    const resp = await apiFetch<{ data: { message: string; proposal_step_id: string } }>(
      `/sessions/${sessionId}/steps/unreject`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },
  /**
   * Fetch session list from the backend session endpoint.
   */
  listSessions: async (): Promise<SessionTree> => {
    const resp = await apiFetch<{ data: SessionTreeApiModel }>('/sessions');
    return mapSessionTree(resp.data);
  },

  getSession: async (sessionId: string): Promise<ProposalSession> => {
    const resp = await apiFetch<{ data: SessionApiModel }>(`/sessions/${sessionId}`);
    return mapProposalSession(resp.data);
  },

  getFoundryResponseDetails: async (sessionId: string): Promise<FoundryResponseDetails> => {
    const resp = await apiFetch<{ data: FoundryResponseDetailsApiModel }>(
      `/sessions/${sessionId}/response-details`
    );
    return mapFoundryResponseDetails(resp.data);
  },
};

type StepApiModel = {
  step_id: string;
  session_id: string;
  operation: ProposalStep['operation'];
  logical_target_id?: string;
  target_id_label?: string;
  node_type?: string;
  node_properties?: Record<string, unknown>;
  from_node_id?: string;
  to_node_id?: string;
  relationship_type?: string;
  field_name?: string;
  field_value?: unknown;
  field_previous_value?: unknown;
  justification?: string;
  created_at?: string;
  review_status?: string;
  rejection_reason?: string;
  rejected_at?: string;
};

type StepGroupApiModel = {
  group_id: string;
  group_type?: ProposalStepGroup['groupType'];
  label?: string;
  steps?: StepApiModel[];
};

type SessionApiModel = {
  session_id: string;
  status?: string;
  title?: string;
  agent_author?: boolean;
  agent_summary?: string;
  approval_justification?: string;
  approved_by?: string;
  approved_at?: string;
  submitted_by?: string;
  submitted_at?: string;
  source?: string;
  source_document_id?: string;
  source_index?: string;
  source_chunk_id?: string;
  proposed_author?: string;
  initiating_human_display?: string;
  initiating_human_subject?: string;
  initiating_human_email?: string;
  invocation_channel?: string;
  authenticated_service_actor?: string;
  authenticated_service_subject?: string;
  depends_upon_session?: string;
  error_stage?: string;
  error_detail?: string;
  errored_at?: string;
  validation_status?: string;
  validated_at?: string;
  validation_warnings?: string[];
  step_groups?: StepGroupApiModel[];
};

type SessionTreeApiModel = {
  session?: SessionApiModel | null;
  selected_session_id?: string;
  branches?: SessionTreeApiModel[];
};

function mapProposalStep(step: StepApiModel): ProposalStep {
  return {
    step_id: step.step_id,
    session_id: step.session_id,
    operation: step.operation,
    logical_target_id: step.logical_target_id || step.target_id_label,
    node_type: step.node_type,
    node_properties: step.node_properties,
    from_node_id: step.from_node_id,
    to_node_id: step.to_node_id,
    relationship_type: step.relationship_type,
    field_name: step.field_name,
    field_value: step.field_value,
    field_previous_value: step.field_previous_value,
    justification: step.justification,
    created_at: step.created_at,
    review_status: step.review_status,
    rejection_reason: step.rejection_reason,
    rejected_at: step.rejected_at,
  };
}

function mapProposalStepGroup(group: StepGroupApiModel): ProposalStepGroup {
  return {
    groupId: group.group_id,
    groupType: group.group_type,
    label: group.label,
    steps: (group.steps ?? []).map(mapProposalStep),
  };
}

function mapProposalSession(session: SessionApiModel): ProposalSession {
  return {
    sessionId: session.session_id,
    status: session.status,
    title: session.title,
    agentAuthor: Boolean(session.agent_author),
    agentSummary: session.agent_summary,
    approvalJustification: session.approval_justification,
    approvedBy: session.approved_by,
    approvedAt: session.approved_at,
    submittedBy: session.submitted_by ?? 'Unknown',
    submittedAt: session.submitted_at ?? '',
    initiatingHumanDisplay: session.initiating_human_display,
    initiatingHumanSubject: session.initiating_human_subject,
    initiatingHumanEmail: session.initiating_human_email,
    invocationChannel: session.invocation_channel,
    dependsUponSession: session.depends_upon_session,
    errorStage: session.error_stage,
    errorDetail: session.error_detail,
    erroredAt: session.errored_at,
    source: session.source,
    sourceDocumentId: session.source_document_id,
    sourceIndex: session.source_index,
    sourceChunkId: session.source_chunk_id,
    proposedAuthor: session.proposed_author,
    authenticatedServiceActor: session.authenticated_service_actor,
    authenticatedServiceSubject: session.authenticated_service_subject,
    validationStatus: session.validation_status,
    validatedAt: session.validated_at,
    validationWarnings: session.validation_warnings ?? [],
    stepGroups: (session.step_groups ?? []).map(mapProposalStepGroup),
  };
}

function mapSessionTree(session: SessionTreeApiModel): SessionTree {
  return {
    session: session.session ? mapProposalSession(session.session) : null,
    selectedSessionId: session.selected_session_id,
    branches: (session.branches ?? []).map(mapSessionTree),
  };
}

function mapFoundryResponseDetails(model: FoundryResponseDetailsApiModel): FoundryResponseDetails {
  return {
    sessionId: model.session_id,
    responseId: model.response_id,
    status: model.status,
    agentName: model.agent_name,
    model: model.model,
    startedAt: model.started_at,
    completedAt: model.completed_at,
    durationMs: model.duration_ms,
    error: model.error,
    finalOutput: model.final_output,
    toolCalls: (model.tool_calls ?? []).map((toolCall) => ({
      name: toolCall.name,
      status: toolCall.status,
      startedAt: toolCall.started_at,
      completedAt: toolCall.completed_at,
      durationMs: toolCall.duration_ms,
      argumentsPreview: toolCall.arguments_preview,
      resultPreview: toolCall.result_preview,
      rawArguments: toolCall.raw_arguments,
      rawResult: toolCall.raw_result,
      error: toolCall.error,
    })),
    raw: model.raw,
  };
}

export const mcpApi = {
  findSystemByName: async (systemName: string): Promise<SystemLookupResult> => {
    return mcpFetch<SystemLookupResult>('find_system_by_name', { system_name: systemName });
  },
};

/**
 * Curation API — index/document browsing and Gap Agent analysis trigger.
 */
export const curationApi = {
  listIndexes: async (): Promise<CurationIndex[]> => {
    const resp = await apiFetch<{ data: { indexes: CurationIndex[] } }>('/content/indexes');
    return resp.data.indexes ?? [];
  },

  listDocuments: async (indexName: string, limit = 100): Promise<CurationDocument[]> => {
    const params = new URLSearchParams({ limit: String(limit) });
    const resp = await apiFetch<{ data: { documents: CurationDocument[] } }>(
      `/content/indexes/${encodeURIComponent(indexName)}/documents?${params.toString()}`
    );
    return resp.data.documents ?? [];
  },

  getDocument: async (indexName: string, documentID: string): Promise<CurationDocument> => {
    const resp = await apiFetch<{ data: { document: CurationDocument } }>(
      `/content/indexes/${encodeURIComponent(indexName)}/documents/${encodeURIComponent(documentID)}`
    );
    return resp.data.document;
  },

  analyzeSession: async (
    sessionId: string,
    req: AnalyzeSessionRequest
  ): Promise<AnalyzeSessionResponse> => {
    const resp = await apiFetch<{ data: AnalyzeSessionResponse }>(
      `/content/sessions/${sessionId}/analyze`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  summarizeNode: async (
    sessionId: string,
    req: SummarizeNodeRequest
  ): Promise<SummarizeNodeResponse> => {
    const resp = await apiFetch<{ data: SummarizeNodeResponse }>(
      `/agents/summary/sessions/${sessionId}/analyze`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  backfillSummaries: async (req: BackfillSummariesRequest): Promise<BackfillSummariesResponse> => {
    const resp = await apiFetch<{ data: BackfillSummariesResponse }>('/agents/summary/backfill', {
      method: 'POST',
      body: JSON.stringify(req),
    });
    return resp.data;
  },

  addDocument: async (
    indexName: string,
    req: AddFreeformDocumentRequest
  ): Promise<AddFreeformDocumentResponse> => {
    const resp = await apiFetch<{ data: AddFreeformDocumentResponse }>(
      `/content/indexes/${encodeURIComponent(indexName)}/documents`,
      {
        method: 'POST',
        body: JSON.stringify(req),
      }
    );
    return resp.data;
  },

  resetChunk: async (
    indexName: string,
    documentId: string,
    chunkId: string,
    options?: { clearRejections?: boolean }
  ): Promise<void> => {
    await apiFetch(
      `/content/indexes/${encodeURIComponent(indexName)}/documents/${encodeURIComponent(documentId)}/chunks/${encodeURIComponent(chunkId)}/reset`,
      {
        method: 'POST',
        body: JSON.stringify({ clearRejections: options?.clearRejections ?? true }),
      }
    );
  },

  deleteDocument: async (indexName: string, documentId: string): Promise<void> => {
    await apiFetch(
      `/content/indexes/${encodeURIComponent(indexName)}/documents/${encodeURIComponent(documentId)}`,
      {
        method: 'DELETE',
      }
    );
  },
};

export const rejectionApi = {
  getBySourceDocument: async (sourceDocumentID: string): Promise<RejectionRecord[]> => {
    const params = new URLSearchParams({ source_document_id: sourceDocumentID });
    const resp = await apiFetch<{ rejections: RejectionRecord[] }>(
      `/rejections?${params.toString()}`
    );
    return resp.rejections ?? [];
  },

  getBySession: async (sessionID: string): Promise<RejectionRecord[]> => {
    const params = new URLSearchParams({ session_id: sessionID });
    const resp = await apiFetch<{ rejections: RejectionRecord[] }>(
      `/rejections?${params.toString()}`
    );
    return resp.rejections ?? [];
  },
};

export const justificationApi = {
  getBySourceDocument: async (sourceDocumentID: string): Promise<JustificationRecord[]> => {
    const params = new URLSearchParams({ source_document_id: sourceDocumentID });
    const resp = await apiFetch<{ justifications: JustificationRecord[] }>(
      `/justifications?${params.toString()}`
    );
    return resp.justifications ?? [];
  },
};

/**
 * Search API — text search across live graph nodes.
 * Calls GET /api/v1/search?q=&top_k= and unwraps the { data: [...] } envelope.
 */
export const searchApi = {
  /**
   * Search live nodes by text query.
   * Uses the nodeTextSearch fulltext index (case-insensitive, tokenised).
   * @param q    Search text (required)
   * @param topK Max results to return (default 20)
   */
  search: async (q: string, topK = 5, includePending = false, sessionId?: string | null): Promise<SearchResult[]> => {
    const params = new URLSearchParams({ q, top_k: String(topK) });
    if (includePending) {
      params.set('include_pending', 'true');
    }
    if (sessionId && sessionId !== 'live') {
      params.set('session_id', sessionId);
    }
    const resp = await apiFetch<{ data: SearchResult[] | null }>(`/search?${params.toString()}`);
    return resp.data ?? [];
  },
};

export const agentApi = {
  search: async (query: string, topK = 10, sessionId?: string | null): Promise<GraphSearchResponse> => {
    const payload: { query: string; top_k: number; session_id?: string } = { query, top_k: topK };
    if (sessionId && sessionId !== 'live') {
      payload.session_id = sessionId;
    }
    const response = await apiFetch<{ data: GraphSearchResponse }>('/agent/search', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.data;
  },
};

/**
 * Admin API — privileged operational endpoints (admin role required).
 */
export const adminApi = {
  listAuthorizationUsers: async (): Promise<Array<{ subject: string; display_name: string; email: string; roles: string[]; organization_ids: string[] }>> => {
    const response = await apiFetch<{ data: Array<{ subject: string; display_name: string; email: string; roles: string[]; organization_ids: string[] }> }>('/admin/authorization/users');
    return response.data;
  },

  setAuthorizationRole: async (subject: string, role: string, granted: boolean, reason = ''): Promise<void> => {
    await apiFetch('/admin/authorization/roles', { method: 'PUT', body: JSON.stringify({ subject, role, granted, reason }) });
  },

  setOrganizationMembership: async (subject: string, organizationID: string, organizationName: string, granted: boolean, reason = ''): Promise<void> => {
    await apiFetch('/admin/authorization/memberships', { method: 'PUT', body: JSON.stringify({ subject, organization_id: organizationID, organization_name: organizationName, granted, reason }) });
  },

  listAuthorizationDecisions: async (subject = '', offset = 0): Promise<{ data: Array<{ subject: string; action: string; object_kind: string; object_id: string; allowed: boolean; reason: string; decided_at: string }>; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams({ limit: '50', offset: String(offset) });
    if (subject) params.set('subject', subject);
    return apiFetch(`/admin/authorization/decisions?${params}`);
  },

  listAuthorizationHistory: async (): Promise<Array<{ subject: string; display_name?: string; record_kind: string; role?: string; organization_id?: string; status: string; actor?: string; reason?: string; occurred_at: string }>> => {
    const response = await apiFetch<{ data: Array<{ subject: string; display_name?: string; record_kind: string; role?: string; organization_id?: string; status: string; actor?: string; reason?: string; occurred_at: string }> }>('/admin/authorization/history?limit=50');
    return response.data;
  },

  requestAuthorizationRole: async (role: string): Promise<void> => {
    await apiFetch('/authorization/requests', { method: 'POST', body: JSON.stringify({ role }) });
  },

  listMyAccessRequests: async (): Promise<{ data: Array<{ request_id: string; role: string; status: string }> }> => {
    return apiFetch('/authorization/requests?status=pending&limit=50');
  },

  listAccessRequests: async (status = 'pending'): Promise<{ data: Array<{ request_id: string; subject: string; display_name?: string; email: string; role: string; status: string; requested_at: string; decided_at?: string; decided_by?: string; decision_reason?: string }>; total: number; limit: number; offset: number }> => {
    const params = new URLSearchParams({ status, limit: '50' });
    return apiFetch(`/admin/authorization/requests?${params}`);
  },

  decideAccessRequest: async (requestID: string, granted: boolean, reason = ''): Promise<void> => {
    await apiFetch('/admin/authorization/requests', { method: 'PUT', body: JSON.stringify({ request_id: requestID, granted, reason }) });
  },

  /**
   * POST /api/v1/admin/backfill-embeddings
   * Generates vector embeddings for live nodes that have none.
   * Returns the count of newly-embedded nodes.
   */
  backfillEmbeddings: async (): Promise<{ backfilled: number }> => {
    const resp = await apiFetch<{ data: { backfilled: number } }>('/admin/backfill-embeddings', {
      method: 'POST',
    });
    return resp.data;
  },

  /**
   * DELETE /api/v1/admin/rejected-sessions
   * Removes all Session nodes with status 'rejected'.
   * Returns the count of deleted sessions.
   */
  deleteRejectedSessions: async (): Promise<{ deleted: number }> => {
    const resp = await apiFetch<{ data: { deleted: number } }>('/admin/rejected-sessions', {
      method: 'DELETE',
    });
    return resp.data;
  },

  /**
   * POST /api/v1/admin/reset-user-pipeline
   * Development reset of user-document testing state.
   */
  resetUserPipeline: async (): Promise<{
    neo4j_sessions_deleted: number;
    source_user_chunks_reset: number;
    rejections_deleted: number;
    justifications_deleted: number;
    warnings?: string[];
  }> => {
    const resp = await apiFetch<{
      data: {
        neo4j_sessions_deleted: number;
        source_user_chunks_reset: number;
        rejections_deleted: number;
        justifications_deleted: number;
        warnings?: string[];
      };
    }>('/admin/reset-user-pipeline', { method: 'POST' });
    return resp.data;
  },
};

/**
 * Health check
 */
export const healthApi = {
  check: async (): Promise<boolean> => {
    const url = `${API_BASE_URL}/health`;
    const response = await fetch(url);
    return response.ok;
  },
};

// ---------------------------------------------------------------------------
// Me API — returns the authenticated caller's backend-resolved identity/role
// ---------------------------------------------------------------------------
export interface MeResponse {
  sub: string;
  email: string;
  /** ABAC permission set. Always contains "viewer"; may also contain "proposer", "curator", "mcp-agent". */
  permissions: string[];
  is_service_account: boolean;
}

export const meApi = {
  /**
   * GET /api/v1/me — returns the caller's identity and backend-authoritative role.
   * Requires a valid bearer token.
   */
  getMe: (): Promise<MeResponse> => apiFetch<MeResponse>('/me'),
};

// ---------------------------------------------------------------------------
// MCP key — shared bearer token for map/configure MCP clients
// ---------------------------------------------------------------------------
export const mcpKeyApi = {
  /**
   * GET /api/v1/mcp-key — returns the shared MCP bearer token.
   * Available to any authenticated user; needed for local MCP client setup.
   */
  getKey: (): Promise<{ token: string }> => apiFetch<{ token: string }>('/mcp-key'),
};
